// Куда ушло время записи ролика.
//
// Запись состоит из стадий с РАЗНОЙ природой: подготовка суток ходит в сеть и
// ждёт чужую очередь, цикл кадров ждёт карту, кодировщик считает. Пока они
// сливаются в одну полоску, вопрос «почему ролик пишется четыре минуты»
// отвечается догадкой — а догадка тут особенно дешева, потому что у каждой
// стадии свой правдоподобный потолок: 60 секунд на дорисовку одной даты, 1200 мс
// на смену суток в кадре, подпор очереди кодировщика.
//
// Здесь только учёт. Ни карта, ни кодировщик о модуле не знают, а сам он не
// знает ни одной стадии по имени — иначе список стадий пришлось бы держать в
// двух местах.

/** Итог по одной стадии. */
export interface StageTiming {
  stage: string
  /** Суммарное время стадии, мс. */
  ms: number
  /** Сколько раз стадия выполнялась (у кадровых — число кадров). */
  calls: number
}

export interface ExportClock {
  /** Замерить один заход стадии. Возвращает то же, что и обёрнутая работа. */
  measure<T>(stage: string, work: () => Promise<T>): Promise<T>
  /** Замерить синхронный заход. */
  measureSync<T>(stage: string, work: () => T): T
  /** Итоги, от самой дорогой стадии к самой дешёвой. */
  report(): StageTiming[]
  /** Сколько прошло с создания часов, мс. */
  total(): number
}

/** Часы записи. `now` подменяется в тестах — `performance.now` там неуправляем. */
export function makeClock(now: () => number = () => performance.now()): ExportClock {
  const started = now()
  const sums = new Map<string, StageTiming>()

  const add = (stage: string, ms: number) => {
    const row = sums.get(stage)
    if (row) {
      row.ms += ms
      row.calls += 1
    } else {
      sums.set(stage, { stage, ms, calls: 1 })
    }
  }

  return {
    async measure(stage, work) {
      const t0 = now()
      try {
        return await work()
      } finally {
        add(stage, now() - t0)
      }
    },
    measureSync(stage, work) {
      const t0 = now()
      try {
        return work()
      } finally {
        add(stage, now() - t0)
      }
    },
    report() {
      // Сортировка по стоимости, а не по порядку выполнения: вопрос к отчёту
      // всегда один — «что тут самое дорогое», и порядок обязан отвечать на него
      // сразу, без чтения всех строк.
      return [...sums.values()].sort((a, b) => b.ms - a.ms)
    },
    total() {
      return now() - started
    },
  }
}

/** Секунды с одним знаком: миллисекунды на четырёхминутной записи — шум.
 *
 *  `s`, а не «с»: строка НЕ переводится и в каталог не идёт (см. `timingSummary`),
 *  поэтому русская буква сделала бы её наполовину локализованной — и наполовину
 *  непонятной там, где кабинет говорит по-английски. Ровно того же держится
 *  `failureDetail` со своими `stage=` и `frame=`. */
function sec(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`
}

/** Доля стадии от всей записи, целыми процентами. */
function share(ms: number, total: number): number {
  return total > 0 ? Math.round((ms / total) * 100) : 0
}

/** Отчёт одной строкой — её человек перешлёт, а мы прочитаем.
 *
 *  Тот же приём, что у `failureDetail`: техническая метка для разбора, а не
 *  подпись интерфейса, — поэтому в каталог переводов она не идёт и опирается на
 *  нейтральные токены (`total=`, `other=`, `s`). Своего канала телеметрии у
 *  клиентской записи нет, консоль чужой машины нам недоступна, и строка под
 *  кнопкой — единственный способ узнать, где на ЧУЖОЙ машине ушло время.
 *
 *  Нейтральность здесь не вкусовая: страж каталогов (`test_cabinet_i18n_guard`)
 *  запрещает кириллицу в коде кабинета, и запрещает справедливо — русское слово
 *  в этой строке сделало бы её наполовину локализованной, то есть наполовину
 *  непонятной на английской витрине.
 *
 *  Стадии, не дотянувшие до порога, схлопываются в «прочее»: отчёт из десяти
 *  строк по полпроцента читается хуже, чем из трёх по тридцать.
 */
export function timingSummary(
  rows: StageTiming[],
  totalMs: number,
  minShare: number = 5,
): string {
  if (!rows.length) return ''
  const big = rows.filter(r => share(r.ms, totalMs) >= minShare)
  const restMs = rows.filter(r => !big.includes(r)).reduce((s, r) => s + r.ms, 0)
  const parts = big.map(r => `${r.stage}=${sec(r.ms)}(${share(r.ms, totalMs)}%)`)
  if (restMs > 0) parts.push(`other=${sec(restMs)}`)
  return `total=${sec(totalMs)} · ${parts.join(' · ')}`
}
